import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '../api/services';
import { MapPin, Eye, EyeOff, CheckCircle2, AlertCircle, ArrowLeft, Phone, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5 mb-10">
    <div className="w-9 h-9 bg-forest-800 rounded-xl flex items-center justify-center">
      <MapPin size={17} className="text-forest-300" />
    </div>
    <div className="flex flex-col leading-none">
      <span className="font-serif font-bold text-forest-900 text-lg">
        TourEnt <span className="text-rust-500">Hub</span>
      </span>
      <span className="text-[9px] font-sans uppercase tracking-[0.15em] text-forest-500">
        Tourism Entrepreneurship Hub
      </span>
    </div>
  </Link>
);

const PasswordRule = ({ met, label }) => (
  <div className={`flex items-center gap-2 text-xs transition-colors ${met ? 'text-forest-600' : 'text-forest-400'}`}>
    <div className={`w-1.5 h-1.5 rounded-full transition-colors ${met ? 'bg-forest-600' : 'bg-cream-300'}`} />
    {label}
  </div>
);

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();

  // Email flow params
  const token  = searchParams.get('token') || '';
  const userId = searchParams.get('id')    || '';

  // SMS flow params
  const method = searchParams.get('method') || 'email'; // 'email' | 'sms' | 'email-otp'
  const phoneParam = searchParams.get('phone') || '';
  const emailParam = searchParams.get('email') || '';

  const isSmsFlow      = method === 'sms';
  const isEmailOtpFlow = method === 'email-otp';
  const isOtpFlow      = isSmsFlow || isEmailOtpFlow;

  const [otp,      setOtp]      = useState('');
  const [phone,    setPhone]    = useState(phoneParam);
  const [emailAddr, setEmailAddr] = useState(emailParam);
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [done,     setDone]     = useState(false);
  const [error,    setError]    = useState('');
  const [resending, setResending] = useState(false);

  const rules = [
    { label: 'At least 8 characters',        met: password.length >= 8 },
    { label: 'One uppercase letter (A–Z)',    met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter (a–z)',    met: /[a-z]/.test(password) },
    { label: 'One number (0–9)',              met: /\d/.test(password) },
    { label: 'One special character (!@#$)', met: /[^a-zA-Z0-9]/.test(password) },
  ];
  const allRulesMet   = rules.every(r => r.met);
  const passwordsMatch = password === confirm && confirm.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesMet)    { setError('Password does not meet requirements.'); return; }
    if (!passwordsMatch) { setError('Passwords do not match.'); return; }

    setError('');
    setLoading(true);
    try {
      if (isSmsFlow) {
        if (!otp.trim() || otp.length !== 6) { setError('Please enter the 6-digit OTP.'); setLoading(false); return; }
        await authService.resetPasswordByOtp(phone, otp, password);
      } else if (isEmailOtpFlow) {
        if (!otp.trim() || otp.length !== 6) { setError('Please enter the 6-digit OTP.'); setLoading(false); return; }
        await authService.resetPasswordByOtpEmail(emailAddr, otp, password);
      } else {
        if (!token || !userId) { setError('Invalid reset link.'); setLoading(false); return; }
        await authService.resetPassword(userId, token, password);
      }
      setDone(true);
      toast.success('Password reset! Please sign in with your new password.');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || (isOtpFlow
        ? 'Invalid or expired OTP. Please try again.'
        : 'Reset link is invalid or has expired.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      if (isEmailOtpFlow) {
        await authService.forgotPassword(emailAddr);
      } else {
        await authService.forgotPasswordByPhone(phone);
      }
      toast.success('New OTP sent!');
      setOtp('');
    } catch {
      toast.success('New OTP sent!'); // always success
    } finally {
      setResending(false); }
  };

  const invalidEmailLink = !isOtpFlow && (!token || !userId);

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Logo />

        {/* Invalid email link */}
        {invalidEmailLink && !done && (
          <div className="text-center space-y-5">
            <div className="w-16 h-16 bg-rust-50 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={32} className="text-rust-400" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-forest-900 mb-2">Invalid reset link</h2>
              <p className="font-sans text-sm text-forest-500">
                This link is missing required parameters. Please request a new reset.
              </p>
            </div>
            <Link to="/forgot-password" className="btn-primary inline-flex">Request New Link</Link>
          </div>
        )}

        {/* Success */}
        {done && (
          <div className="text-center space-y-5">
            <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} className="text-forest-600" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-forest-900 mb-2">Password updated!</h2>
              <p className="font-sans text-sm text-forest-500">Redirecting you to sign in...</p>
            </div>
            <Link to="/login" className="btn-primary inline-flex">Sign In Now →</Link>
          </div>
        )}

        {/* Reset form */}
        {!invalidEmailLink && !done && (
          <>
            <h1 className="font-serif text-3xl text-forest-900 mb-1">Set new password</h1>
            <p className="font-sans text-sm text-forest-500 mb-8">
              {isSmsFlow
                ? `Enter the 6-digit OTP sent to ${phone} and your new password.`
                : isEmailOtpFlow
                ? `Enter the 6-digit OTP texted to your phone and your new password.`
                : 'Choose a strong password for your TourEnt Hub account.'}
            </p>

            {error && (
              <div className="flex items-start gap-3 bg-rust-50 border border-rust-200 text-rust-600 rounded-xl px-4 py-3 text-sm mb-5">
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* OTP field (SMS or email-OTP flow) */}
              {isOtpFlow && (
                <div>
                  <label className="block font-sans text-sm font-medium text-forest-700 mb-1.5">
                    6-Digit OTP Code
                  </label>
                  <input
                    type="text" inputMode="numeric" pattern="[0-9]*"
                    maxLength={6} value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full px-4 py-3 rounded-xl border border-cream-300 font-sans text-lg text-center tracking-[0.4em] text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-forest-400 flex items-center gap-1">
                      <Phone size={11} /> {isSmsFlow ? `Sent to ${phone}` : 'Sent by SMS to your phone on file'}
                    </p>
                    <button type="button" onClick={handleResendOtp} disabled={resending}
                      className="text-xs text-forest-600 hover:text-forest-900 font-medium flex items-center gap-1 transition-colors">
                      {resending ? <Loader2 size={11} className="animate-spin" /> : null}
                      Resend OTP
                    </button>
                  </div>
                </div>
              )}

              {/* New password */}
              <div>
                <label className="block font-sans text-sm font-medium text-forest-700 mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'} required
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-xl border border-cream-300 font-sans text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white pr-11"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-forest-400 hover:text-forest-700">
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-1.5">
                    {rules.map((r, i) => <PasswordRule key={i} met={r.met} label={r.label} />)}
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="block font-sans text-sm font-medium text-forest-700 mb-1.5">Confirm Password</label>
                <input
                  type={showPwd ? 'text' : 'password'} required
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  className={`w-full px-4 py-3 rounded-xl border font-sans text-sm text-forest-900 focus:outline-none bg-white transition-colors ${
                    confirm.length > 0
                      ? passwordsMatch ? 'border-forest-400' : 'border-rust-300'
                      : 'border-cream-300 focus:border-forest-500'
                  }`}
                />
                {confirm.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-rust-500 mt-1.5">Passwords do not match</p>
                )}
                {confirm.length > 0 && passwordsMatch && (
                  <p className="text-xs text-forest-500 mt-1.5 flex items-center gap-1">
                    <CheckCircle2 size={11} className="text-forest-600" /> Passwords match
                  </p>
                )}
              </div>

              <button type="submit"
                disabled={loading || !allRulesMet || !passwordsMatch || (isOtpFlow && otp.length !== 6)}
                className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none">
                {loading
                  ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Updating...</>
                  : 'Set New Password'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-sans text-forest-500 hover:text-forest-800 transition-colors">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;